import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import {
  createZeroAuthSessionRequest,
  refreshZeroAuthSessionRequest,
} from "@ano/shared/api/zero-auth";
import { API_URL } from "@/lib/config";

const ZERO_SYNC_TOKEN_STORAGE_KEY = "ano-zero-sync-token";

/**
 * Wrapped in a mutable object so tests can override `enabled` between cases.
 * Production code reads `enabled` at every check site, so the test override
 * propagates. Vitest can't easily mock `import.meta.env.VITE_*` because Vite
 * inlines those values at build time.
 */
export const zeroSessionAuthFlag: { enabled: boolean } = {
  enabled:
    (import.meta.env.VITE_ENABLE_ZERO_SESSION_AUTH as string | undefined) ===
    "true",
};

const REFRESH_BUFFER_MS = 120_000; // refresh 2 min before expiry
const MIN_REFRESH_DELAY_MS = 10_000;
const RETRY_DELAY_MS = 30_000;
const EXPIRY_SAFETY_MARGIN_MS = 60_000; // discard tokens within 60s of expiry

/**
 * Decodes a JWT's base64url payload. Handles both standard base64 and
 * URL-safe base64 (RFC 7515 §2). Returns null on any decode failure.
 */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const parts = jwt.split(".");
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = JSON.parse(atob(b64));
    return typeof json === "object" && json !== null
      ? (json as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Returns the `sub` claim of a WorkOS access token (`null` if the token is
 * missing, malformed, or has no `sub`). Exported for tests.
 */
function workosSubFromAccessToken(accessToken: string | null): string | null {
  if (!accessToken) return null;
  const payload = decodeJwtPayload(accessToken);
  return typeof payload?.sub === "string" ? payload.sub : null;
}

/**
 * Reads the cached Zero session token from localStorage and validates:
 *   1. `exp` is still in the future (with a 60s safety margin)
 *   2. `sub` matches the supplied `currentWorkosSub` (when provided) — a
 *      mismatch means the token was minted for a previous user, so loading
 *      it would put Zero into a `needs-auth` loop (`JWTClaimValidationFailed:
 *      unexpected "sub" claim value`). The prod-us-cutover re-signup bug
 *      from 2026-05-14: WorkOS-delete + same-email-resignup gives a new
 *      `user.id`, but `/api/zero-auth/refresh` preserves the old `sub`, so
 *      the cached + every refreshed token kept failing until the user
 *      manually cleared localStorage. Discard the token here so the mint
 *      path runs against the fresh WorkOS access token.
 *
 * Pass `currentWorkosSub = null` to skip the sub check — used during the
 * brief first-render window before the WorkOS access token has resolved.
 * The hook's `accessToken`-change effect re-validates and discards stale
 * tokens once the sub becomes available.
 *
 * Returns `null` (and clears the storage) for any failure mode.
 */
function loadStoredZeroToken(currentWorkosSub: string | null): string | null {
  if (!zeroSessionAuthFlag.enabled) return null;
  const stored = localStorage.getItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
  if (!stored) return null;
  const payload = decodeJwtPayload(stored);
  if (!payload || typeof payload.exp !== "number") {
    localStorage.removeItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
    return null;
  }
  if (
    currentWorkosSub !== null &&
    (typeof payload.sub !== "string" || payload.sub !== currentWorkosSub)
  ) {
    console.info(
      "[ZeroSessionToken] discarded cached token: sub mismatch (stored=",
      payload.sub,
      "current=",
      currentWorkosSub,
      ")",
    );
    localStorage.removeItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
    return null;
  }
  const expiresAtMs = payload.exp * 1000;
  if (expiresAtMs - Date.now() < EXPIRY_SAFETY_MARGIN_MS) {
    localStorage.removeItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
    return null;
  }
  console.info(
    "[ZeroSessionToken] loaded from localStorage, exp:",
    new Date(expiresAtMs).toISOString(),
  );
  return stored;
}

export interface UseZeroSessionTokenResult {
  /** The Zero session JWT (or null if not yet minted / disabled / failed). */
  token: string | null;
  /** True once the initial mint attempt has completed (success or failure). */
  resolved: boolean;
  /** Force a refresh and return a token. Used by ZeroAuthRecovery. */
  refreshToken: () => Promise<string | null>;
}

/**
 * Manages a separate Zero sync session token (12h TTL) minted from the
 * WorkOS access token. Decouples Zero sync auth from WorkOS access token
 * expiry so that backgrounded tabs don't lose sync state when the WorkOS
 * token's short TTL elapses.
 *
 * Gated by `VITE_ENABLE_ZERO_SESSION_AUTH=true`. When disabled, this hook
 * is mostly a no-op — but `refreshToken()` still force-refreshes the WorkOS
 * token (used as a fallback by ZeroAuthRecovery).
 *
 * Architecture:
 * - On mount: load any valid stored Zero JWT from localStorage
 * - If no stored token but a fresh WorkOS token exists: mint via /api/zero-auth/session
 * - Schedule auto-refresh 2 min before expiry
 * - On 401/403 from Zero, ZeroAuthRecovery calls refreshToken() which:
 *   1. Tries /api/zero-auth/refresh with the existing Zero JWT (if any)
 *   2. Falls back to /api/zero-auth/session AFTER force-refreshing the WorkOS token
 *
 * Critical Feb 9 lesson: NEVER call /api/zero-auth/session with a cached
 * (possibly stale) WorkOS token. Always force-refresh first. If the force
 * refresh fails, return null — do NOT fall back to the cached value.
 */
export function useZeroSessionToken(
  accessToken: string | null,
  accessTokenResolved: boolean,
): UseZeroSessionTokenResult {
  const { getAccessToken } = useAuth();
  const [token, setToken] = useState<string | null>(() =>
    loadStoredZeroToken(workosSubFromAccessToken(accessToken)),
  );
  const [resolved, setResolved] = useState(!zeroSessionAuthFlag.enabled);
  const accessTokenRef = useRef<string | null>(accessToken);
  const zeroTokenRef = useRef<string | null>(token);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);
  // Stable handle to the main effect's `run` function so `refreshToken`'s
  // post-recovery timer can re-enter the same self-perpetuating chain
  // (`run → scheduleRefresh → setTimeout → run`). Without this, the
  // post-recovery timer would only fire once and the proactive refresh loop
  // would die — see Bug #2 in the Phase B plan.
  const runRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);
  useEffect(() => {
    zeroTokenRef.current = token;
    if (token) {
      localStorage.setItem(ZERO_SYNC_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
    }
  }, [token]);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  // When the feature is disabled, ensure we don't leave any stale state
  // around from a previous session where it was enabled.
  useEffect(() => {
    if (!zeroSessionAuthFlag.enabled) {
      localStorage.removeItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
      clearRefreshTimer();
    }
  }, []);

  /**
   * Mint a fresh Zero session JWT via /api/zero-auth/session.
   *
   * CRITICAL: always force-refresh the WorkOS token first. This is the Feb 9
   * fix — using the cached `accessTokenRef.current` here was the original bug
   * that caused infinite 401 recovery loops. On force-refresh failure, return
   * null and let the caller bail. Do NOT fall back to the cached value.
   */
  const requestSession = useMemo(
    () => async () => {
      let workosToken: string | null;
      try {
        console.info("[ZeroSessionToken] forcing WorkOS refresh before mint");
        workosToken = await getAccessTokenRef.current({ forceRefresh: true });
      } catch (err) {
        console.warn(
          "[ZeroSessionToken] forceRefresh failed; cannot mint Zero JWT:",
          err,
        );
        return null;
      }
      if (!workosToken) return null;
      const session = await createZeroAuthSessionRequest({
        apiBaseUrl: API_URL,
        accessToken: workosToken,
      });
      setToken(session.token);
      console.info(
        "[ZeroSessionToken] minted, exp:",
        new Date(session.expires_at).toISOString(),
      );
      return session;
    },
    [],
  );

  /**
   * Refresh the existing Zero session JWT via /api/zero-auth/refresh. Uses
   * only the existing Zero JWT — no WorkOS interaction. Returns null if
   * there's no stored Zero token to refresh, OR if the refresh endpoint
   * rejects (e.g., the stored Zero JWT itself expired). Returning null
   * (rather than throwing) lets callers fall through to `requestSession()`
   * to mint a fresh JWT instead of bailing.
   */
  const refreshWithZeroToken = useMemo(
    () => async () => {
      const currentZeroToken = zeroTokenRef.current;
      if (!currentZeroToken) return null;
      try {
        const session = await refreshZeroAuthSessionRequest({
          apiBaseUrl: API_URL,
          zeroToken: currentZeroToken,
        });
        setToken(session.token);
        console.info(
          "[ZeroSessionToken] refreshed via /refresh, exp:",
          new Date(session.expires_at).toISOString(),
        );
        return session;
      } catch (err) {
        console.info(
          "[ZeroSessionToken] /refresh failed, will fall through to mint:",
          err,
        );
        return null;
      }
    },
    [],
  );

  // The main effect tracks presence of an access token, not its value. Every
  // time the WorkOS access token rotates (~every 5 min for ~10 min TTL),
  // `accessToken` becomes a new string. If we included it in the effect deps,
  // the effect would tear down its scheduled 12h refresh and immediately call
  // `run()` → /refresh — silently re-coupling Zero's refresh schedule to
  // WorkOS's. That defeats the entire point of the session-token path.
  //
  // Reacting to `hasAccessToken` (a boolean) means the effect only re-runs on
  // the null↔non-null transitions (sign-in / sign-out), not on rotations.
  // Inside the effect, `accessTokenRef.current` holds the latest value — the
  // ref-update effect at line 125 runs before this effect in the same flush.
  const hasAccessToken = accessToken !== null;

  // Initial mint + scheduled refresh effect.
  useEffect(() => {
    let cancelled = false;
    clearRefreshTimer();

    if (!zeroSessionAuthFlag.enabled) {
      return;
    }

    if (!accessTokenResolved) {
      queueMicrotask(() => {
        if (cancelled) return;
        setResolved(false);
      });
      return;
    }
    if (!accessTokenRef.current) {
      queueMicrotask(() => {
        if (cancelled) return;
        // Only clear the Zero token if we don't already have a valid one.
        // This preserves the stored JWT during transient auth gaps (initial
        // load where getAccessToken() is slow or fails). Sign-out is handled
        // by ZeroProvider unmounting entirely (key={user.id}), so this path
        // doesn't need to handle sign-out cleanup.
        if (!zeroTokenRef.current) {
          setToken(null);
        }
        setResolved(true);
      });
      return;
    }

    const scheduleRetry = () => {
      clearRefreshTimer();
      refreshTimerRef.current = setTimeout(() => {
        void run();
      }, RETRY_DELAY_MS);
    };

    const scheduleRefresh = (expiresAtMs: number) => {
      clearRefreshTimer();
      const refreshInMs = Math.max(
        expiresAtMs - Date.now() - REFRESH_BUFFER_MS,
        MIN_REFRESH_DELAY_MS,
      );
      refreshTimerRef.current = setTimeout(() => {
        console.info("[ZeroSessionToken] scheduled refresh firing");
        void run();
      }, refreshInMs);
    };

    const run = async () => {
      try {
        const session =
          (await refreshWithZeroToken()) ?? (await requestSession());
        if (cancelled) return;
        if (session) {
          setResolved(true);
          scheduleRefresh(session.expires_at);
        } else {
          setToken(null);
          setResolved(true);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("[ZeroSessionToken] run() failed:", err);
        scheduleRetry();
        setResolved(true);
      }
    };

    // Publish `run` so `refreshToken` can re-enter the chain after recovery.
    // Cleared on teardown so a stale closure can't fire after the effect
    // re-runs (e.g., when accessToken changes).
    runRef.current = run;
    void run();

    return () => {
      cancelled = true;
      runRef.current = null;
      clearRefreshTimer();
    };
  }, [
    hasAccessToken,
    accessTokenResolved,
    refreshWithZeroToken,
    requestSession,
  ]);

  // Re-validate the cached Zero JWT's `sub` against the live WorkOS access
  // token whenever the access token changes. The main effect above only
  // reacts to `hasAccessToken` (null↔non-null) to avoid re-coupling Zero's
  // 12h refresh to WorkOS's 10-min rotation, so it WON'T re-fire on a
  // same-string-to-same-string user swap (the prod-us-cutover re-signup
  // case from 2026-05-14 — WorkOS-delete + same-email-resignup yields a
  // new user.id but `hasAccessToken` stays true). This effect catches
  // that. Pure local JWT decode — no round-trip.
  //
  // On mismatch: clear the cached JWT synchronously (both the ref AND
  // localStorage so a concurrent refresh path can't pick up the stale
  // value mid-flight) and re-enter the main effect's `run()` via
  // `runRef`. Since `refreshWithZeroToken` reads `zeroTokenRef.current`,
  // clearing the ref forces it to return null and fall through to
  // `requestSession()`, which force-refreshes WorkOS and mints a new JWT
  // bound to the current user's sub.
  useEffect(() => {
    if (!zeroSessionAuthFlag.enabled) return;
    const stored = zeroTokenRef.current;
    if (!stored) return;
    const currentSub = workosSubFromAccessToken(accessToken);
    if (currentSub === null) return; // no sub yet — wait for next change
    const storedPayload = decodeJwtPayload(stored);
    const storedSub =
      typeof storedPayload?.sub === "string" ? storedPayload.sub : null;
    if (storedSub !== null && storedSub !== currentSub) {
      console.info(
        "[ZeroSessionToken] sub mismatch on access-token change — clearing & re-minting (stored=",
        storedSub,
        "current=",
        currentSub,
        ")",
      );
      zeroTokenRef.current = null;
      localStorage.removeItem(ZERO_SYNC_TOKEN_STORAGE_KEY);
      setToken(null);
      void runRef.current?.();
    }
  }, [accessToken]);

  /**
   * Refresh entry point used by ZeroAuthRecovery. Returns the freshest
   * available token. Uses a Promise mutex so concurrent callers (e.g.
   * ZeroAuthRecovery firing simultaneously with a scheduled refresh) share
   * the same in-flight operation.
   *
   * When zeroSessionAuthFlag.enabled=false, falls back to a force-refreshed
   * WorkOS token (PR #765 fix).
   *
   * Wrapped in `useCallback` so the prop reference passed to
   * `ZeroAuthRecovery` and `ZeroVisibilityRefresh` is stable across renders.
   * Without this, both components' effect deps churn on every parent render
   * and re-register their visibilitychange/connection-state listeners. The
   * deps array lists `refreshWithZeroToken` and `requestSession` for
   * exhaustive-deps compliance, but both are themselves `useMemo([])`
   * singletons so the callback identity is stable in practice. All other
   * dependencies inside the body are refs or module-level globals.
   */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (refreshPromiseRef.current) {
      console.info(
        "[ZeroSessionToken] refreshToken: in-flight, returning shared promise",
      );
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      try {
        if (!zeroSessionAuthFlag.enabled) {
          // The Zero session token path is disabled, so we use the WorkOS
          // access token directly. Force-refresh otherwise we'd return the
          // same expired value that triggered recovery.
          try {
            const fresh = await getAccessTokenRef.current({
              forceRefresh: true,
            });
            if (fresh) return fresh;
          } catch (err) {
            console.warn(
              "[ZeroSessionToken] forceRefresh fallback failed:",
              err,
            );
          }
          return accessTokenRef.current;
        }

        try {
          const session =
            (await refreshWithZeroToken()) ?? (await requestSession());
          if (session) {
            clearRefreshTimer();
            const refreshInMs = Math.max(
              session.expires_at - Date.now() - REFRESH_BUFFER_MS,
              MIN_REFRESH_DELAY_MS,
            );
            refreshTimerRef.current = setTimeout(() => {
              console.info(
                "[ZeroSessionToken] scheduled refresh firing (post-recovery)",
              );
              // Re-enter the main effect's run() so the chain self-perpetuates
              // via scheduleRefresh. If runRef is null, the effect has been
              // torn down (e.g., accessToken changed) — the new effect's own
              // run() will already be active, so bail.
              void runRef.current?.();
            }, refreshInMs);
            return session.token;
          }
        } catch (err) {
          console.warn("[ZeroSessionToken] refreshToken inner failed:", err);
        }

        // Last-resort fallback: at this point both refreshWithZeroToken AND
        // requestSession have failed (or returned null). The caller will use
        // whatever we return. accessTokenRef.current is the WorkOS token —
        // it might still be valid even if we couldn't mint a Zero session.
        return accessTokenRef.current;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [refreshWithZeroToken, requestSession]);

  return { token, resolved, refreshToken };
}

// Re-export for tests
export {
  decodeJwtPayload,
  loadStoredZeroToken,
  workosSubFromAccessToken,
  ZERO_SYNC_TOKEN_STORAGE_KEY,
};
